import React, { useEffect, useState } from "react";
import { Download, Share, Plus, X } from "lucide-react";

const DISMISS_KEY = "sm_pwa_dismissed_at";
const DISMISS_TTL_DAYS = 14;

function isStandalone() {
    if (typeof window === "undefined") return false;
    return (
        window.matchMedia?.("(display-mode: standalone)").matches ||
        window.navigator.standalone === true
    );
}

function isIOS() {
    if (typeof navigator === "undefined") return false;
    const ua = navigator.userAgent || "";
    return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}

function isMobile() {
    if (typeof navigator === "undefined") return false;
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent || "");
}

function wasDismissedRecently() {
    try {
        const v = localStorage.getItem(DISMISS_KEY);
        if (!v) return false;
        const days = (Date.now() - Number(v)) / (1000 * 60 * 60 * 24);
        return days < DISMISS_TTL_DAYS;
    } catch {
        return false;
    }
}

export default function PWAInstallBanner() {
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [show, setShow] = useState(false);
    const [iosMode, setIosMode] = useState(false);

    useEffect(() => {
        if (isStandalone() || wasDismissedRecently()) return;

        // Android / desktop Chrome / Edge — listen for beforeinstallprompt
        const onPrompt = (e) => {
            e.preventDefault();
            setDeferredPrompt(e);
            setShow(true);
        };
        window.addEventListener("beforeinstallprompt", onPrompt);

        // iOS Safari — never fires beforeinstallprompt. Show manual instructions
        // a bit later (only on mobile iOS, after a small UX delay).
        let iosTimer;
        if (isIOS() && isMobile()) {
            iosTimer = setTimeout(() => {
                setIosMode(true);
                setShow(true);
            }, 2500);
        }

        const onInstalled = () => {
            setShow(false);
            setDeferredPrompt(null);
        };
        window.addEventListener("appinstalled", onInstalled);

        return () => {
            window.removeEventListener("beforeinstallprompt", onPrompt);
            window.removeEventListener("appinstalled", onInstalled);
            if (iosTimer) clearTimeout(iosTimer);
        };
    }, []);

    const handleInstall = async () => {
        if (!deferredPrompt) return;
        deferredPrompt.prompt();
        try {
            await deferredPrompt.userChoice;
        } catch {
            // ignore
        }
        setDeferredPrompt(null);
        setShow(false);
    };

    const dismiss = () => {
        try {
            localStorage.setItem(DISMISS_KEY, String(Date.now()));
        } catch {
            // ignore
        }
        setShow(false);
    };

    if (!show) return null;

    return (
        <div
            data-testid="pwa-install-banner"
            className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[60] w-[calc(100%-2rem)] max-w-md"
        >
            <div className="relative bg-zinc-900/95 backdrop-blur-md border border-orange-500/30 rounded-xl shadow-2xl shadow-orange-500/10 p-4 pr-10">
                <button
                    type="button"
                    onClick={dismiss}
                    aria-label="Fermer"
                    data-testid="pwa-install-dismiss"
                    className="absolute top-2 right-2 text-zinc-500 hover:text-zinc-200 p-1"
                >
                    <X size={16} />
                </button>

                <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex-shrink-0 rounded-lg bg-gradient-to-br from-orange-600 to-red-600 flex items-center justify-center">
                        <Download size={20} className="text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="text-[10px] uppercase tracking-[0.25em] text-orange-500 font-bold mb-1">
                            Installer Sponsormania
                        </div>
                        {iosMode ? (
                            <p className="text-sm text-zinc-300 leading-relaxed">
                                Ajoutez Sponsormania à votre écran d&apos;accueil :
                                appuyez sur <Share size={14} className="inline mx-1 -mt-1" />
                                puis « Sur l&apos;écran d&apos;accueil »
                                <Plus size={14} className="inline mx-1 -mt-1" />.
                            </p>
                        ) : (
                            <>
                                <p className="text-sm text-zinc-300 mb-3 leading-relaxed">
                                    Ajoutez l&apos;app à votre écran d&apos;accueil pour un accès instantané.
                                </p>
                                <button
                                    type="button"
                                    onClick={handleInstall}
                                    data-testid="pwa-install-accept"
                                    className="bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-500 hover:to-red-500 text-white font-bold uppercase tracking-widest text-xs px-4 py-2 rounded-lg transition-all active:scale-[0.98]"
                                >
                                    Installer
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
